import React, { useMemo } from "react";
import {
  Container,
  Typography,
  ListItemIcon,
  Button,
  MenuItem,
  Box,
  Tooltip,
  IconButton,
  Grid,
  TextField,
  Chip,
} from "@mui/material";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from "@mui/icons-material/Refresh";
import DetalleRendicion from "../detailPanel/DetalleRendicion";

function TablaRendicionesExtendidas({
  data,
  loading,
  desde,
  hasta,
  setDesde,
  setHasta,
  onUpdate,
  onNuevaRendicion,
  onVerPedidos,
  onCompletarDatos,
  onVerResumen,
  onVerResumenRendicion,
  onMarcarInactivo,
  onPasarPedidos,
  columns,
  tableHeadCellProps,
  muiSearchTextFieldProps,
  customLocalization,
}) {
  const columnsExtendidas = useMemo(() => {
    // Filtrar la columna 'fecha' del original
    const columnsWithoutFecha = columns.filter(col => col.accessorKey !== "fecha");
    
    // Crear nuevas columnas
    const fechaInicioColumn = {
      accessorKey: "fecha",
      header: "Fecha Inicio",
      size: 100,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? value : "No registrada";
      },
    };

    const fechaFinColumn = {
      accessorKey: "fecha_fin",
      header: "Fecha Fin",
      size: 100,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? value : "No registrada";
      },
    };

    // Devolver: fecha_inicio, fecha_fin, y luego el resto de columnas (sin fecha)
    return [fechaInicioColumn, fechaFinColumn, ...columnsWithoutFecha];
  }, [columns]);

  const initialState = {
    density: "compact",
    pagination: { pageIndex: 0, pageSize: 50 },
    grouping: ["nombre_suc"],
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ mb: 2, mt: 2 }}>
      <MaterialReactTable
        columns={columnsExtendidas}
        data={data ?? []}
        layoutMode={"grid"}
        initialState={initialState}
        enableColumnActions={false}
        enableGlobalFilterModes={false}
        enableHiding={false}
        enableRowActions={true}
        enableGrouping={true}
        enablePinning={true}
        enableColumnFilters={false}
        enableFullScreenToggle={false}
        positionActionsColumn={"last"}
        positionToolbarAlertBanner="none"
        stateIsLoading={loading}
        enableExpandAll={false}
        localization={customLocalization}
        enableToolbarInternalActions={true}
        muiTableHeadCellProps={tableHeadCellProps}
        muiSearchTextFieldProps={muiSearchTextFieldProps}
        enableDensityToggle={false}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            backgroundColor:
              row.original.estado === 272
                ? "#e3f2fd"
                : row.original.estado === 273
                ? "#ffff88"
                : row.original.estado === 274
                ? "#acec98"
                : null,
          },
        })}
        muiTableDetailPanelProps={{
          sx: {
            backgroundColor: "white",
            width: "100%",
          },
        }}
        enableColumnResizing={true}
        renderDetailPanel={({ row }) => (
          <DetalleRendicion row={row.original} />
        )}
        muiTableBodyCellProps={({ row }) => ({
          align: "left",
        })}
        renderTopToolbarCustomActions={() => (
          <>
            <Typography variant="poster" fontSize={26}>
              Rendiciones Extendidas
            </Typography>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item>
                <Button 
                  startIcon={<AddCircleOutlineIcon/>} 
                  size="small" 
                  variant="contained" 
                  color="primary" 
                  onClick={onNuevaRendicion}
                >
                  Nueva Rendición
                </Button>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="Desde"
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="Hasta"
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Tooltip title="Actualizar">
                  <IconButton
                    onClick={onUpdate}
                    sx={{
                      backgroundColor: "#e0e0e0",
                      "&:hover": { backgroundColor: "#d5d5d5" },
                    }}
                  >
                    <RefreshIcon sx={{ color: "#333" }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}
        renderRowActionMenuItems={({ closeMenu, row, index }) => [
          <MenuItem
            key={index}
            onClick={() => {
              onVerPedidos(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <RemoveRedEyeIcon color="primary" />
            </ListItemIcon>
            Ver Pedidos
          </MenuItem>,
          <MenuItem
            key={index}
            onClick={() => {
              onCompletarDatos(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <CreateNewFolderIcon color="secondary" />
            </ListItemIcon>
            Completar Datos
          </MenuItem>,
          <MenuItem
            key={index}
            onClick={() => {
              onVerResumen(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <SummarizeIcon color="warning" />
            </ListItemIcon>
            Resumen de Pagos
          </MenuItem>,
          <MenuItem
            key={index}
            onClick={() => {
              onVerResumenRendicion(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <ChecklistIcon color="success" />
            </ListItemIcon>
            Resumen de Rendición
          </MenuItem>,
          <MenuItem
            key={index}
            disabled={row.original.estado === 273 || row.original.activo === 0}
            onClick={() => {
              onMarcarInactivo(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <DoDisturbIcon color="error" />
            </ListItemIcon>
            Marcar Inactiva
          </MenuItem>,
          <MenuItem
            key={index}
            style={{
              display: row.original.activo === 0 ? null : "none",
            }}
            disabled={row.original.estado === 274}
            onClick={() => {
              onPasarPedidos(row.original);
              closeMenu();
            }}
            sx={{ m: 0 }}
          >
            <ListItemIcon>
              <ChangeCircleIcon color="primary" />
            </ListItemIcon>
            Pasar Pedidos
          </MenuItem>,
        ]}
      />
    </Container>
  );
}

export default TablaRendicionesExtendidas;